import { getWorkspaceIdFromCurrentUrl, getWorkspaceIdFromUrl, useCurrentWorkspace } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Accordion,
  AccordionContent,
  AccordionGroup,
  AccordionTitle,
  Box,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Scrollable,
  Slider,
  Stack,
  Tag,
  Text,
  TextLabel,
  InlineStack,
} from '@customerio/pluma-components/react';
import { useCallback, useState } from 'react';
import { type Reference } from '.';
import { useMatches } from 'react-router-dom';
import { useAIProfileSelection } from '@/pages/journeys/settings/account-verification/hooks';
import { workspaceStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import { calc } from '@vanilla-extract/css-utils';
import { getToneDisplayScore, type ToneDimensionKey } from '@/pages/journeys/settings/ai-business-profile/helpers/form';
import type {
  AIContextOverrides,
  PlatformKey,
  PlatformsOverride,
  ToneOverride,
} from '@/pages/journeys/settings/ai-business-profile/types';
import { isEmpty, merge } from 'lodash-es';

type TextOverrideKey = keyof Omit<
  AIContextOverrides,
  'tone' | 'tone_examples' | 'platforms' | 'nsfw' | 'regions' | 'languages'
>;

const overrideLabels: Record<TextOverrideKey, string> = {
  name: 'Business Name',
  legal_name: 'Legal Name',
  long_description: 'Business Description',
  industry: 'Industry',
  website_url: 'Website',
  privacy_policy_url: 'Privacy Policy',
  terms_of_service_url: 'Terms of Service',
  pricing_url: 'Pricing',
  download_url: 'Download',
  audience: 'Audience',
};

const CONTEXT_POPOVER_SECTIONS: { heading: string; keys: TextOverrideKey[] }[] = [
  {
    heading: 'Basic info',
    keys: ['name', 'legal_name', 'website_url', 'industry', 'long_description', 'audience'],
  },
  {
    heading: 'Key links',
    keys: ['privacy_policy_url', 'terms_of_service_url', 'pricing_url', 'download_url'],
  },
];

const PLATFORM_KEYS_ORDER: PlatformKey[] = ['ios', 'android', 'mac', 'windows', 'web', 'browserExtension', 'api'];

const platformLabels: Record<PlatformKey, string> = {
  ios: 'iOS',
  android: 'Android',
  mac: 'Mac',
  windows: 'Windows',
  web: 'Web app',
  browserExtension: 'Browser extension',
  api: 'API',
};

function getPlatformEntries(platforms: PlatformsOverride | undefined): [PlatformKey, string][] {
  if (platforms == null) return [];
  return PLATFORM_KEYS_ORDER.filter((key) => {
    const link = platforms[key]?.link;
    return link != null && link.length > 0;
  }).map((key) => [key, platforms[key]!.link!]);
}

const TONE_SLIDER_CONFIG: {
  key: ToneDimensionKey;
  label: string;
  description: string;
  leftLabel: string;
  rightLabel: string;
}[] = [
  {
    key: 'formality',
    label: 'Formality',
    description: 'How formal or casual your communication style is',
    leftLabel: 'Casual',
    rightLabel: 'Formal',
  },
  {
    key: 'humor',
    label: 'Humor',
    description: 'How playful or witty the tone is',
    leftLabel: 'Serious',
    rightLabel: 'Humorous',
  },
  {
    key: 'respect',
    label: 'Respect',
    description: 'How cheeky or bold the brand voice is',
    leftLabel: 'Irreverent',
    rightLabel: 'Very Respectful',
  },
  {
    key: 'energy',
    label: 'Energy',
    description: 'Overall vibe and enthusiasm level',
    leftLabel: 'Calm & Direct',
    rightLabel: 'Enthusiastic',
  },
];

function hasToneOverrides(tone: ToneOverride | undefined): boolean {
  if (tone == null) return false;
  return TONE_SLIDER_CONFIG.some(({ key }) => tone[key]?.score != null);
}

/**
 * The goal of this component is to display the used business profile context in the chat if it was used.
 * The overall goal is to drive assistant usage and engagement, and this UI is subject to change quickly hence
 * the current lack of test coverage.
 */
export function BusinessProfileUsage({ references }: { references: Reference[] }) {
  const firstReference = references.at(0);
  const firstReferenceURL =
    firstReference?.url != null && URL.canParse(firstReference.url) ? new URL(firstReference?.url).pathname : undefined;

  const { track } = useAnalytics();
  const safeNavigate = useSafeNavigate();

  const workspaces = useStore(workspaceStore);
  const currentWorkspace = useCurrentWorkspace();
  const workspaceIdFromUrl = getWorkspaceIdFromCurrentUrl();

  const currentWorkspaceId =
    firstReferenceURL != null
      ? Number(getWorkspaceIdFromUrl(firstReferenceURL))
      : currentWorkspace?.id ?? workspaceIdFromUrl;
  const safeURL = firstReferenceURL ?? `/workspaces/${currentWorkspaceId}/settings/ai-business-profile`;

  const { selectedProfile } = useAIProfileSelection(workspaces, currentWorkspaceId);

  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];

  const [isOpen, setIsOpen] = useState(false);

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        track('aiAssistant', 'contextUsedPopoverOpened', {});
      }
      setIsOpen(open);
    },
    [track],
  );

  if (selectedProfile == null) {
    return null;
  }

  const baseContext =
    selectedProfile.body.ai_context != null && !isEmpty(selectedProfile.body.ai_context)
      ? selectedProfile.body.ai_context
      : {};

  const overrides = selectedProfile.body.overrides ?? {};

  const aiContext = merge({}, baseContext, overrides);

  const textOverrides = Object.fromEntries(
    Object.entries(aiContext ?? {}).filter(([_, value]) => typeof value === 'string' && value.length > 0),
  ) as Partial<Record<TextOverrideKey, string>>;

  const platformEntries = getPlatformEntries(aiContext?.platforms);
  const tone = aiContext?.tone;
  const showToneSection = hasToneOverrides(tone);

  return (
    <Popover placement="left" withArrow={false} isOpen={isOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger>
        <Tag cursor="pointer">Context Used</Tag>
      </PopoverTrigger>
      <PopoverContent
        px="0"
        pt="0"
        pb="150"
        overflowWrap="words"
        style={{
          width: calc.multiply(themeVars.space['100'], 45),
          maxWidth: calc.multiply(themeVars.space['100'], 55),
        }}
      >
        <Box>
          <AccordionGroup gap="0" shouldAllowMultipleOpen={false}>
            {CONTEXT_POPOVER_SECTIONS.map(({ heading, keys }) => {
              const entries = keys
                .filter((key): key is TextOverrideKey => key in textOverrides && textOverrides[key] != null)
                .map((key) => [key, textOverrides[key]!] as const);
              if (entries.length === 0) return null;
              return (
                <Accordion
                  key={heading}
                  onToggle={({ isOpen: accordionOpen }) => {
                    if (accordionOpen) {
                      track('aiAssistant', 'contextUsedAccordionClicked', { section: heading });
                    }
                  }}
                >
                  <AccordionTitle p="150">{heading}</AccordionTitle>
                  <AccordionContent p="150">
                    <Scrollable
                      overflow="y"
                      withEdgeIndicators
                      style={{ maxHeight: calc.multiply(themeVars.space['100'], 26) }}
                    >
                      <Stack gap="100">
                        {entries.map(([key, value]) => (
                          <Stack gap="025" key={key}>
                            <Text text="product-pxs">{overrideLabels[key]}</Text>
                            {key.endsWith('_url') ? (
                              <Link variant="secondary" href={value} target="_blank">
                                {value}
                              </Link>
                            ) : (
                              <Text text="product-p">{value}</Text>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    </Scrollable>
                  </AccordionContent>
                </Accordion>
              );
            })}
            {showToneSection ? (
              <Accordion
                key="tone-and-voice"
                onToggle={({ isOpen: accordionOpen }) => {
                  if (accordionOpen) {
                    track('aiAssistant', 'contextUsedAccordionClicked', { section: 'Tone & Voice' });
                  }
                }}
              >
                <AccordionTitle p="150">Tone & Voice</AccordionTitle>
                <AccordionContent px="150" pt="150">
                  <Scrollable
                    overflow="y"
                    withEdgeIndicators
                    style={{ maxHeight: calc.multiply(themeVars.space['100'], 30) }}
                  >
                    <Stack gap="300">
                      {TONE_SLIDER_CONFIG.filter(({ key }) => tone?.[key]?.score != null).map(
                        ({ key, label, leftLabel, rightLabel, description }) => (
                          <Stack gap="050" key={key}>
                            <InlineStack alignInline="space-between">
                              <TextLabel as="span" variant="secondary" whiteSpace="nowrap">
                                {leftLabel}
                              </TextLabel>
                              <TextLabel as="span" variant="secondary" whiteSpace="nowrap">
                                {rightLabel}
                              </TextLabel>
                            </InlineStack>
                            <Slider
                              name={`tone-${key}-context`}
                              value={getToneDisplayScore(key, tone![key]!.score)}
                              minValue={1}
                              maxValue={5}
                              step={1}
                              isDisabled
                              ariaLabel={`${label} slider`}
                            />
                            <Text color="subtle" size="xs" id={`${key}-desc`}>
                              {description}
                            </Text>
                          </Stack>
                        ),
                      )}
                    </Stack>
                  </Scrollable>
                </AccordionContent>
              </Accordion>
            ) : null}
            {platformEntries.length > 0 ? (
              <Accordion
                key="platform-availability"
                onToggle={({ isOpen: accordionOpen }) => {
                  if (accordionOpen) {
                    track('aiAssistant', 'contextUsedAccordionClicked', { section: 'Platform availability' });
                  }
                }}
              >
                <AccordionTitle p="150">Platform availability</AccordionTitle>
                <AccordionContent p="150">
                  <Scrollable
                    overflow="y"
                    withEdgeIndicators
                    style={{ maxHeight: calc.multiply(themeVars.space['100'], 30) }}
                  >
                    <Stack gap="100">
                      {platformEntries.map(([key, link]) => (
                        <Stack gap="025" key={key}>
                          <Text text="product-pxs">{platformLabels[key]}</Text>
                          <Link variant="secondary" href={link} target="_blank">
                            {link}
                          </Link>
                        </Stack>
                      ))}
                    </Stack>
                  </Scrollable>
                </AccordionContent>
              </Accordion>
            ) : null}
          </AccordionGroup>
          <Link
            isDisabled={lastMatch?.id === 'workspace_settings_ai_business_profile'}
            onClick={() => {
              track('aiAssistant', 'editBusinessProfileClicked', { 'Click Location': 'context_used_popover' });
              setIsOpen(false);
              safeNavigate(safeURL);
            }}
            href={safeURL}
            icon="exit"
            iconPosition="trailing"
            display="inline-block"
            pt="150"
            pl="150"
            justifyContent="flex-start"
          >
            Edit Business Profile
          </Link>
        </Box>
      </PopoverContent>
    </Popover>
  );
}
